import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, Eye, Loader2 } from "lucide-react";
import {
  useUpdateReportSchedule,
  useDeleteReportSchedule,
  usePreviewReportSchedule,
} from "@/hooks/use-api";
import type { ReportSchedule } from "@/types/api";
import { ScheduleDialog } from "./schedule-dialog";

interface ScheduleTableProps {
  schedules: ReportSchedule[];
  isLoading: boolean;
  isMsp?: boolean;
}

const REPORT_TYPE_LABELS: Record<string, string> = {
  posture: "Posture",
  msp_portfolio: "MSP Portfolio",
};

function formatDate(iso: string | null) {
  if (!iso) return "--";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function ScheduleTable({
  schedules,
  isLoading,
  isMsp,
}: ScheduleTableProps) {
  const [editSchedule, setEditSchedule] = useState<ReportSchedule | null>(null);
  const updateMutation = useUpdateReportSchedule();
  const deleteMutation = useDeleteReportSchedule();
  const previewMutation = usePreviewReportSchedule();

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (schedules.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
        No report schedules configured. Create one to receive automated reports.
      </div>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Type</TableHead>
            <TableHead>Frequency</TableHead>
            <TableHead>Recipients</TableHead>
            <TableHead>Next Run</TableHead>
            <TableHead>Last Run</TableHead>
            <TableHead>Enabled</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {schedules.map((s) => (
            <TableRow key={s.id}>
              <TableCell>
                <Badge variant="outline">
                  {REPORT_TYPE_LABELS[s.report_type] ?? s.report_type}
                </Badge>
              </TableCell>
              <TableCell className="capitalize">{s.frequency}</TableCell>
              <TableCell>
                <span title={s.recipients.join(", ")}>
                  {s.recipients.length <= 2
                    ? s.recipients.join(", ")
                    : `${s.recipients[0]} +${s.recipients.length - 1}`}
                </span>
              </TableCell>
              <TableCell>{formatDate(s.next_run_at)}</TableCell>
              <TableCell>{formatDate(s.last_run_at)}</TableCell>
              <TableCell>
                <Switch
                  checked={s.enabled}
                  onCheckedChange={(enabled) =>
                    updateMutation.mutate({
                      scheduleId: s.id,
                      enabled,
                    })
                  }
                />
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Preview"
                    onClick={() => previewMutation.mutate(s.id)}
                    disabled={previewMutation.isPending}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Edit"
                    onClick={() => setEditSchedule(s)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Delete"
                    onClick={() => {
                      if (confirm("Delete this schedule?")) {
                        deleteMutation.mutate(s.id);
                      }
                    }}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4 text-red-400" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <ScheduleDialog
        open={!!editSchedule}
        onOpenChange={(open) => {
          if (!open) setEditSchedule(null);
        }}
        schedule={editSchedule}
        isMsp={isMsp}
      />
    </>
  );
}
